import InternalLink from "components/InternalLink";
import { getVocabulary } from "components/Vocabulary";
import usePaginationControls from "hooks/usePaginationControls";
import useTableCustomizationHelper from "hooks/useTableCustomizationHelper";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import FindingAPI from "restAPI/FindingAPI";
import { FindingSummary } from "types/api/findingSummary";
import { SortInfo } from "types/pagination";
import { CellRenderers } from "utils/tableUtils";
import { getUrlSearch } from "utils/urlUtil";
import { FINDING_STATUS_DISPLAY_TEXT } from "views/Findings/Constants";
import { getSortParamForAPI } from "views/Findings/FindingList/FindingListProvider";
import EnvironmentName from "views/GlobalSettings/EnvironmentName";

import ACLink from "ac-components/ACLink/ACLink";
import ACPaginationBar from "ac-components/ACPaginationBar/ACPaginationBar";
import ACSeverityTag, { SeverityType } from "ac-components/ACSeverityTag";
import ACTable, { ACTableColumnType } from "ac-components/ACTable/ACTable";
import ACButton from "ac-components/ac-button/ACButton";
import RedirectIcon from "ac-components/ac-icons/RedirectIcon";
import ACResizableColumnCell from "ac-components/ac-resizable-column-cell/ACResizableColumnCell";

import { showResurfaceTimeForAcceptRisk } from "../utils";
import { useFindingDetails } from "./FindingDetailsProvider";

export default function SimilarVersionFindingsTable() {
  const { id, finding } = useFindingDetails();
  const [dataSource, setDataSource] = useState<FindingSummary[]>([]);
  const [totalElements, setTotalElements] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);

  const paginationControls = usePaginationControls({ defaultPageSize: 10 });
  const { page, pageSize, onPageChange, onSortChange, sortInfo, resetFilters } =
    paginationControls;

  // Reset pagination when finding changes
  useEffect(() => {
    resetFilters();
  }, [finding, resetFilters]);

  // Fetch data
  useEffect(() => {
    if (!id) return;

    setLoading(true);
    const sortColumns = getSortParamForAPI(sortInfo);

    const { promise, cancel } = FindingAPI.getAll({
      filters: {
        versionId: [id],
      },
      sortColumns,
      page: page - 1,
      size: pageSize,
    });

    promise
      .then((res) => {
        setDataSource(res.content || []);
        setTotalElements(res.totalElements || 0);
      })
      .catch(() => {})
      .finally(() => setLoading(false));

    return cancel;
  }, [id, page, pageSize, sortInfo]);

  const columns: ACTableColumnType<FindingSummary>[] = useMemo(() => {
    return [
      {
        title: "ID",
        dataIndex: "id",
        key: "id",
        sorter: true,
        sortType: "number",
        fixed: "left",
        width: 120,
        render: (findingId: number) => (
          <ACLink href={`#/findings/${findingId}`} target="_blank">
            {findingId}
          </ACLink>
        ),
      },
      {
        title: getVocabulary("product"),
        dataIndex: "product",
        key: "product",
        width: 150,
        render: CellRenderers.NAME_ID_MAP,
      },
      {
        title: getVocabulary("subProduct"),
        dataIndex: "subProduct",
        key: "subProduct",
        width: 150,
        render: CellRenderers.NAME_ID_MAP,
      },
      {
        title: "Environment",
        dataIndex: "environmentName",
        key: "environmentName",
        width: 150,
        sorter: true,
        render: (_: unknown, record: FindingSummary) => (
          <EnvironmentName name={record.environment} isToolTip />
        ),
      },
      {
        title: "Status",
        dataIndex: "status",
        key: "status",
        width: 120,
        sorter: true,
        render: (status: string, record: FindingSummary) => {
          const displayText = FINDING_STATUS_DISPLAY_TEXT[status] || status;
          return showResurfaceTimeForAcceptRisk(record) || displayText;
        },
      },
      {
        title: getVocabulary("severity"),
        dataIndex: "severity",
        key: "severity",
        sorter: true,
        sortType: "severity",
        width: 120,
        render: (severity: SeverityType) => <ACSeverityTag value={severity} />,
      },
    ];
  }, []);

  const handleSortChange = useCallback(
    (newSortInfo: SortInfo) => {
      onSortChange(newSortInfo);
    },
    [onSortChange],
  );

  const { columns: customizedColumns } =
    useTableCustomizationHelper<FindingSummary>({
      columns,
      prefKey: "finding-details-similar-version-findings-table",
      skipColumnVisibility: true,
      draggable: false,
    });

  return (
    <div className="similar-version-findings-table">
      <div className="pull-right m-b-sm">
        <InternalLink
          to={`/findings?${getUrlSearch({
            filters: { versionId: [id!] },
          })}`}
          target="_blank"
        >
          <ACButton type="secondary" size="sm">
            <RedirectIcon /> View All
          </ACButton>
        </InternalLink>
      </div>
      <ACTable<FindingSummary>
        dataSource={dataSource}
        columns={customizedColumns}
        rowKey="id"
        loading={loading}
        sortInfo={sortInfo}
        onSortChange={handleSortChange}
        pagination={{ pageSize }}
        autoEnableScroll
        computeTableWidth
        rightScrollShadow
        sticky
        components={{
          header: {
            cell: ACResizableColumnCell,
          },
        }}
      />
      <ACPaginationBar
        total={totalElements}
        page={page}
        pageSize={pageSize}
        onChange={onPageChange}
        showSizeChanger
        stickyBottom
      />
    </div>
  );
}
